import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import * as yup from 'yup'
import {
    Button,
    Checkbox,
    Classes,
    Dialog,
    FormGroup,
    InputGroup,
    Tooltip,
} from '@blueprintjs/core'
import { TimePicker } from '@blueprintjs/datetime'

import {
    addNewAction,
    getAction,
    updateAction,
} from 'src/store/modules/runbooks/actions'
import { getActiveRunbook } from 'src/store/modules/runbooks'
import {
    singleEditAction,
    toggleControl,
} from 'src/store/modules/ui/runbook/controls'
import styles from './addOrEditActionDialog.module.scss'

export const validationSchema = yup.object().shape({
    name: yup
        .string()
        .required(JSON.stringify({ name: 'Name is a required field' }))
        .max(
            200,
            JSON.stringify({
                name: 'Please provide a name of maximum 200 characters',
            })
        ),
    description: yup.string().max(
        512,
        JSON.stringify({
            description:
                'Please provide a description of maximum 512 characters',
        })
    ),
    estimatedDuration: yup
        .string()
        .nullable()
        .transform((value) => value === null ? '' : value)
        .matches(/^\d+$/, {
            message: JSON.stringify({
                estimatedDuration: 'Duration must be a number',
            }),
            excludeEmptyString: true,
        }),
    customUrl: yup
        .string()
        .url(JSON.stringify({ customUrl: 'Must be a valid url' })),
    color: yup.string().matches(/^#([a-fA-F0-9]{6}|[a-fA-F0-9]{3})$/, {
        message: JSON.stringify({
            color: 'Color must be of hexadecimal format',
        }),
        excludeEmptyString: true,
    }),
})

class AddOrEditActionDialog extends Component {
    static propTypes = {
        runbook: PropTypes.object.isRequired,
        action: PropTypes.object,
        updateAction: PropTypes.func.isRequired,
        toggleControl: PropTypes.func.isRequired,
    }

    state = {
        inputValues: {
            name: '',
            description: '',
            customUrl: '',
            estimatedDuration: '',
            color: '',
            doNotStartBeforeTime: null,
            reverse: true,
            autoSucceed: false,
        },
        inputErrors: {},
        isEditing: false,
    }

    componentDidMount() {
        const { action, addingChild, addingSibling } = this.props

        if (action && !(addingChild || addingSibling)) {
            this.setState({
                inputValues: {
                    name: action.name,
                    description: action.description,
                    customUrl: action.customUrl,
                    estimatedDuration: action.estimatedDuration,
                    color: action.color,
                    doNotStartBeforeTime: action.doNotStartBeforeTime
                        ? this.handleDoNotStartBeforeTimeConversion(
                              action.doNotStartBeforeTime
                          )
                        : null,
                    reverse: !!action.reverse,
                    autoSucceed: !!action.autoSucceed,
                },
                isEditing: true,
            })
        }
    }

    handleDoNotStartBeforeTimeConversion = (doNotStartBeforeTime) => {
        if (doNotStartBeforeTime) {
            const timeSplit = doNotStartBeforeTime.split(':')
            doNotStartBeforeTime = new Date()
            doNotStartBeforeTime.setHours(timeSplit[0])
            doNotStartBeforeTime.setMinutes(timeSplit[1])
            doNotStartBeforeTime.setSeconds(0)
        }
        return doNotStartBeforeTime
    }

    handleInputChange = (e) => {
        const { inputValues } = this.state

        this.setState({
            inputValues: { ...inputValues, [e.target.name]: e.target.value },
        })
    }

    handleTimePickerChange = (e) => {
        const { inputValues } = this.state

        this.setState({
            inputValues: { ...inputValues, doNotStartBeforeTime: e },
        })
    }

    handleSubmit = async (e) => {
        e.preventDefault()
        const { inputValues, isEditing } = this.state
        const {
            runbook,
            action,
            openAction,
            addingChild,
            updateAction,
            addNewAction,
        } = this.props

        try {
            await validationSchema.validate(inputValues, { abortEarly: false })
            this.setState({ inputErrors: {} })
        } catch (e) {
            const inputErrors = {}
            Object.assign(
                inputErrors,
                ...e.errors.map((error) => JSON.parse(error))
            )
            this.setState({ inputErrors })
            return
        }

        const estimatedDuration =
            inputValues.estimatedDuration !== ''
                ? parseInt(inputValues.estimatedDuration)
                : undefined

        let doNotStartBeforeTime = null

        if (inputValues.doNotStartBeforeTime !== null) {
            let hour = '' + inputValues.doNotStartBeforeTime.getHours()
            let minute = '' + inputValues.doNotStartBeforeTime.getMinutes()
            if (hour.length < 2) hour = '0' + hour

            if (minute.length < 2) minute = '0' + minute

            doNotStartBeforeTime = `${hour}:${minute}`
        }

        if (isEditing) {
            updateAction(runbook.id, action.uid, {
                ...inputValues,
                estimatedDuration,
                doNotStartBeforeTime,
            })
        } else {
            addNewAction(
                runbook.id,
                {
                    ...inputValues,
                    estimatedDuration,
                    doNotStartBeforeTime,
                    customer: runbook.organization,
                    customerId: runbook.orgPk,
                    reverse: Number(inputValues.reverse),
                    autoSucceed: Number(inputValues.autoSucceed),
                },
                {
                    fromAction: openAction,
                    parent: !!addingChild,
                }
            )
        }

        this.handleClose()
        // } catch (error) {
        //     if (error.inner) {
        //         return this.setState({
        //             inputErrors: this.mapErrorsToFieldNames(error.inner),
        //         })
        //     }
        //     throw new Error(error)
        // }
    }

    handleClose = () => {
        const {
            toggleControl,
            singleEditAction,
            isEditing,
            addingSibling,
            addingChild,
        } = this.props

        isEditing && toggleControl('isEditingBaseFields')
        addingSibling && toggleControl('isInsertingActionAsSibling')
        addingChild && toggleControl('isInsertingActionAsChild')
        singleEditAction(null)
    }

    mapErrorsToFieldNames(inner) {
        const errors = {}
        inner.forEach((error) => (errors[error.path] = error.message))

        return errors
    }

    render() {
        const { inputValues, inputErrors, isEditing } = this.state
        const { action } = this.props

        return (
            <Dialog
                onClose={this.handleClose}
                onOpened={this.handleOpened}
                title={isEditing ? `Edit ${action.name}` : 'Add new action'}
                isOpen={true}
                canOutsideClickClose={false}
            >
                <form onSubmit={this.handleSubmit}>
                    <div className={Classes.DIALOG_BODY}>
                        <FormGroup
                            label="Action name"
                            labelFor="name"
                            helperText={inputErrors.name}
                            intent={inputErrors.name ? 'danger' : 'none'}
                        >
                            <InputGroup
                                value={inputValues.name}
                                placeholder="e.g. Shared Network"
                                id="name"
                                name="name"
                                large
                                onChange={this.handleInputChange}
                                intent={inputErrors.name ? 'danger' : 'none'}
                            />
                        </FormGroup>
                        <FormGroup
                            label="Description"
                            labelFor="description"
                            helperText={inputErrors.description}
                            intent={inputErrors.description ? 'danger' : 'none'}
                        >
                            <InputGroup
                                value={inputValues.description}
                                id="description"
                                name="description"
                                large
                                onChange={this.handleInputChange}
                                intent={
                                    inputErrors.description ? 'danger' : 'none'
                                }
                            />
                        </FormGroup>
                        <FormGroup
                            label="Custom URL"
                            labelFor="custom-url"
                            helperText={inputErrors.customUrl}
                            intent={inputErrors.customUrl ? 'danger' : 'none'}
                        >
                            <InputGroup
                                value={inputValues.customUrl}
                                placeholder="e.g. https://www.customurl.com"
                                id="custom-url"
                                name="customUrl"
                                large
                                onChange={this.handleInputChange}
                                intent={
                                    inputErrors.customUrl ? 'danger' : 'none'
                                }
                            />
                        </FormGroup>
                        <FormGroup label="Action behaviour">
                            <div className={styles.checkboxWrapper}>
                                <Tooltip
                                    content="If this is checked, children will be set to 'In progress' when status of this action goes to ‘In progress‘"
                                    position="bottom"
                                >
                                    <Checkbox
                                        checked={this.state.inputValues.reverse}
                                        label="Auto-launch children"
                                        onChange={() => {
                                            this.setState({
                                                inputValues: {
                                                    ...this.state.inputValues,
                                                    reverse: !this.state
                                                        .inputValues.reverse,
                                                },
                                            })
                                        }}
                                        className={styles.firstCheckbox}
                                    />
                                </Tooltip>
                                {isEditing &&
                                !(action.parents?.length > 0) ? null : (
                                    <Tooltip
                                        content="If this is checked, once the parent actions succeed, this action will move to successful, instead of in progress."
                                        position="bottom"
                                    >
                                        <Checkbox
                                            checked={
                                                this.state.inputValues
                                                    .autoSucceed
                                            }
                                            label="Auto-succeed parent"
                                            onChange={() => {
                                                this.setState({
                                                    inputValues: {
                                                        ...this.state
                                                            .inputValues,
                                                        autoSucceed: !this.state
                                                            .inputValues
                                                            .autoSucceed,
                                                    },
                                                })
                                            }}
                                        />
                                    </Tooltip>
                                )}
                            </div>
                        </FormGroup>
                        <FormGroup
                            label="Estimated duration"
                            labelFor="estimated-duration"
                            helperText={inputErrors.estimatedDuration}
                            intent={
                                inputErrors.estimatedDuration
                                    ? 'danger'
                                    : 'none'
                            }
                            className={styles.estimatedDuration}
                        >
                            <InputGroup
                                value={inputValues.estimatedDuration}
                                placeholder="e.g. 15"
                                id="estimated-duration"
                                name="estimatedDuration"
                                large
                                onChange={this.handleInputChange}
                                intent={
                                    inputErrors.estimatedDuration
                                        ? 'danger'
                                        : 'none'
                                }
                                style={{ width: 70 }}
                            />
                            <span className={styles.minutesSpan}>Minutes</span>
                            <span className={styles.notStartBeforeSpan}>
                                Do not start before (UTC):{' '}
                            </span>
                            <TimePicker
                                value={inputValues.doNotStartBeforeTime}
                                onChange={this.handleTimePickerChange}
                                showArrowButtons={true}
                            />
                            <Tooltip content={'Unset do not start before time'}>
                                <Button
                                    onClick={() =>
                                        this.setState({
                                            inputValues: {
                                                ...inputValues,
                                                doNotStartBeforeTime: null,
                                            },
                                        })
                                    }
                                    icon="small-cross"
                                />
                            </Tooltip>
                        </FormGroup>
                        <FormGroup
                            label="Color"
                            labelFor="color"
                            helperText={inputErrors.color}
                            intent={inputErrors.color ? 'danger' : 'none'}
                        >
                            <InputGroup
                                value={inputValues.color}
                                placeholder="e.g. #000000"
                                id="color"
                                name="color"
                                large
                                onChange={this.handleInputChange}
                                intent={inputErrors.color ? 'danger' : 'none'}
                                style={{ width: 120 }}
                            />
                        </FormGroup>
                    </div>
                    <div className={Classes.DIALOG_FOOTER}>
                        <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                            <Button large intent="primary" type="submit">
                                Save
                            </Button>
                            <Button
                                large
                                intent="lynch-underline"
                                onClick={this.handleClose}
                            >
                                Close
                            </Button>
                        </div>
                    </div>
                </form>
            </Dialog>
        )
    }
}

const mapStateToProps = (state) => ({
    runbook: getActiveRunbook(state),
    action: getAction(
        state,
        getActiveRunbook(state).id,
        state.ui.runbook.controls.singleOpenAction
    ),
    openAction: state.ui.runbook.controls.singleOpenAction,
    isEditing: state.ui.runbook.controls.isEditingBaseFields,
    addingChild: state.ui.runbook.controls.isInsertingActionAsChild,
    addingSibling: state.ui.runbook.controls.isInsertingActionAsSibling,
})

const mapDispatchToProps = (dispatch) =>
    bindActionCreators(
        {
            updateAction,
            toggleControl,
            singleEditAction,
            addNewAction,
        },
        dispatch
    )

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(AddOrEditActionDialog)
